import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useQueue } from '@shared/util/hooks/useQueue';
import { Alert } from './ModalAlert';
import { ModalBackdrop } from './ModalBackdrop';
import { Confirm } from './ModalConfirm';
import { ModalContext } from './ModalContext';
import { ModalQueueItem, useAlertCreator, useConfirmCreator } from './useModalCreator';

export function ModalProvider({ children }: { children: ReactNode }) {
  const queue = useQueue<ModalQueueItem>();
  const alert = useAlertCreator(queue);
  const confirm = useConfirmCreator(queue);
  const value = useMemo(() => ({ alert, confirm }), [alert, confirm]);

  return (
    <ModalContext.Provider value={value}>
      {children}
      <ModalBackdrop />
      {queue.current && <ModalRenderer key={queue.current.id} modal={queue.current} />}
    </ModalContext.Provider>
  );
}

function ModalRenderer({ modal }: { modal: ModalQueueItem }) {
  const [isOpen, setIsOpen] = useState(false);

  const { id, type, ...modalProps } = modal;

  const handleClose = useCallback(async () => {
    await modalProps.onClose?.();
    setIsOpen(false);
  }, [modalProps]);

  useEffect(() => {
    setIsOpen(true);
  }, []);

  const Component = type === 'confirm' ? Confirm : Alert;

  return <Component key={id} {...modalProps} isOpen={isOpen} onClose={handleClose} />;
}
